import { useMsal, useAccount } from '@azure/msal-react';
import { useState, useEffect } from 'react';
import { AppRoles, AppRole, loginRequest } from './msalConfig';
import { useMasquerade } from './MasqueradeContext';
import { setTokenProvider } from '../services/api';

export interface AuthUser {
  name:     string;
  email:    string;
  objectId: string;
  roles:    string[];
}

// Reads the MSAL access token directly from sessionStorage.
// This is a reliable synchronous fallback when acquireTokenSilent
// has timing issues on page load. The token is validated against
// expiry before returning.
function getMsalTokenFromStorage(): string | null {
  try {
    const keys = Object.keys(sessionStorage).filter(k => k.includes('accesstoken'));
    if (keys.length === 0) return null;
    const val = JSON.parse(sessionStorage.getItem(keys[0]) || 'null');
    if (!val?.secret) return null;
    // Check expiry (expiresOn is a Unix timestamp in seconds)
    if (val.expiresOn && val.expiresOn * 1000 < Date.now()) return null;
    return val.secret;
  } catch {
    return null;
  }
}

export function useAuth() {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { masqueradeRole, setMasquerade } = useMasquerade();

  const roles: string[] = (account?.idTokenClaims as any)?.roles || [];

  const user: AuthUser | null = account ? {
    name:     account.name || '',
    email:    account.username || '',
    objectId: account.localAccountId || '',
    roles,
  } : null;

  const hasRole = (role: AppRole): boolean => roles.includes(role);

  const [dbAppRole, setDbAppRole] = useState<string | null>(null);
  const [dbUserID,  setDbUserID]  = useState<number | null>(null);
  const [dbDepartmentID, setDbDepartmentID] = useState<number | null>(null);

  // Register token provider immediately (not in useEffect) so it's available
  // before the first API call fires. Tries acquireTokenSilent first, falls back
  // to reading directly from sessionStorage.
  if (account) {
    setTokenProvider(async () => {
      try {
        const result = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
          forceRefresh: false,
        });
        if (result.accessToken) return result.accessToken;
      } catch {
        // fall through to sessionStorage fallback
      }
      // Reliable fallback — read directly from MSAL's sessionStorage cache
      return getMsalTokenFromStorage();
    });
  }

  useEffect(() => {
    if (!account) return;
    fetch('/api/user/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        objectId:    account.localAccountId,
        email:       account.username,
        displayName: account.name || '',
      }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.AppRole) setDbAppRole(data.AppRole);
        if (data?.UserID)  setDbUserID(data.UserID);
        setDbDepartmentID(data?.DepartmentID ?? null);
      })
      .catch(() => {});
  }, [account]);

  const realIsAdmin = dbAppRole === 'IT.Admin';
  const isAdmin    = masqueradeRole ? false : realIsAdmin;
  // Driven by the DB AppRole field, not the Entra token role claim -- matches
  // isAdmin above and the documented pattern (DECISIONS.md "DB AppRole vs
  // Entra Token Role"). These previously read hasRole(), which checks Entra
  // custom app roles that role changes made in Admin -> Users never touch,
  // so promoting someone to Director/EMT/IT.Approver there had no effect
  // on what they actually saw in the app.
  const isApprover = masqueradeRole
    ? masqueradeRole === 'IT.Approver'
    : (dbAppRole === AppRoles.ITApprover || dbAppRole === AppRoles.ITAdmin);
  const isDirector = masqueradeRole
    ? masqueradeRole === 'Director'
    : (dbAppRole === AppRoles.Director || dbAppRole === AppRoles.ITAdmin);
  const isEMT = masqueradeRole
    ? masqueradeRole === 'EMT'
    : (dbAppRole === AppRoles.EMT || dbAppRole === AppRoles.ITAdmin);

  const isMasquerading = masqueradeRole !== null;
  const effectiveRole  = masqueradeRole ?? dbAppRole;
  const logout         = () => instance.logoutPopup();

  return {
    user,
    dbUserID,
    dbDepartmentID,
    hasRole,
    isAdmin,
    realIsAdmin,
    isApprover,
    isDirector,
    isEMT,
    isMasquerading,
    masqueradeRole,
    effectiveRole,
    setMasquerade,
    logout,
  };
}
